import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, CheckCircle2, MapPin, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "info@heracq.com";

const initial = (service) => ({
  name: "",
  email: "",
  phone: "",
  service_interest: service || "",
  message: "",
});

export default function CareContact({ site }) {
  const [form, setForm] = useState(initial(""));
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const validate = () => {
    if (!form.name.trim()) return "Please share your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
    if (form.phone.trim().length < 5) return "Please enter a valid phone.";
    if (form.message.trim().length < 10)
      return "Please tell us a little more (at least 10 characters).";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        source: site.key, // 'nj' or 'ny'
      });
      setSuccess(true);
      // Open a prefilled mailto so the inquiry also lands in the inbox
      const subject = encodeURIComponent(
        `New ${site.brand} inquiry — ${form.name}`,
      );
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nService: ${form.service_interest || "—"}\nLocation: ${site.region}\n\nMessage:\n${form.message}`,
      );
      // Delay a touch so toast and success state render before the mail client opens
      setTimeout(() => {
        window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
      }, 600);
      toast.success("Thank you — we’ll be in touch within one business day.");
      setForm(initial(""));
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(site.address.mapsQuery)}`;

  return (
    <section
      id="contact"
      data-testid="care-contact"
      className="py-24 md:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <span className="eyebrow">Request care</span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.03] text-[#1A2621]">
            Let&rsquo;s talk
            <span className="italic text-[#284A3B]"> about them.</span>
          </h2>
          <p className="mt-6 text-lg text-[#4A5D54] leading-relaxed max-w-md">
            Share a few details and we&rsquo;ll follow up personally within one
            business day. Your information stays private.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="care-address-link"
              className="flex items-start gap-4 group"
            >
              <span className="w-10 h-10 rounded-full bg-[#E6EAE3] flex items-center justify-center text-[#284A3B] shrink-0">
                <MapPin size={18} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#4A5D54]">
                  {site.productName}
                </div>
                <div className="font-serif-display text-xl text-[#1A2621] mt-1 group-hover:text-[#C86A51] transition-colors">
                  {site.address.street}
                </div>
                <div className="text-[#4A5D54]">{site.address.locality}</div>
              </div>
            </a>

            {site.phone && (
              <a
                href={site.phoneHref}
                data-testid="care-phone-link"
                className="flex items-start gap-4 group"
              >
                <span className="w-10 h-10 rounded-full bg-[#E6EAE3] flex items-center justify-center text-[#284A3B] shrink-0">
                  <Phone size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#4A5D54]">
                    Call us
                  </div>
                  <div className="font-serif-display text-xl text-[#1A2621] mt-1 group-hover:text-[#C86A51] transition-colors">
                    {site.phone}
                  </div>
                </div>
              </a>
            )}

            <a
              href={`mailto:${EMAIL}?subject=Care%20Inquiry%20(${encodeURIComponent(site.region)})`}
              data-testid="care-email-link"
              className="flex items-start gap-4 group"
            >
              <span className="w-10 h-10 rounded-full bg-[#E6EAE3] flex items-center justify-center text-[#284A3B] shrink-0">
                <Mail size={18} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#4A5D54]">
                  Email
                </div>
                <div className="font-serif-display text-xl text-[#1A2621] mt-1 group-hover:text-[#C86A51] transition-colors">
                  {EMAIL}
                </div>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          {success ? (
            <div
              data-testid="care-contact-success"
              className="bg-[#E6EAE3] rounded-2xl border border-[#D5DACF] p-10"
            >
              <div className="w-12 h-12 rounded-full bg-[#284A3B] text-[#FDFBF7] flex items-center justify-center">
                <CheckCircle2 size={22} />
              </div>
              <h3 className="font-serif-display text-3xl mt-6 text-[#1A2621]">
                Thank you — your request is in.
              </h3>
              <p className="mt-4 text-[#4A5D54] max-w-lg">
                A care manager will reach out within one business day. We also
                opened a draft email with your details to{" "}
                <a
                  href={`mailto:${EMAIL}`}
                  className="text-[#284A3B] underline underline-offset-4"
                >
                  {EMAIL}
                </a>
                .
              </p>
              <button
                onClick={() => setSuccess(false)}
                data-testid="care-contact-another"
                className="mt-8 inline-flex items-center gap-2 text-[#284A3B] font-medium hover:text-[#C86A51] transition-colors"
              >
                Send another message <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <form
              data-testid="care-contact-form"
              onSubmit={onSubmit}
              noValidate
              className="bg-white rounded-2xl border border-[#D5DACF] p-8 md:p-10 shadow-sm space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium text-[#4A5D54]">
                    Your name
                  </Label>
                  <Input
                    data-testid="care-form-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className="mt-2 bg-white border-[#D5DACF] rounded-xl h-12"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#4A5D54]">
                    Email
                  </Label>
                  <Input
                    type="email"
                    data-testid="care-form-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@email.com"
                    className="mt-2 bg-white border-[#D5DACF] rounded-xl h-12"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium text-[#4A5D54]">
                    Phone
                  </Label>
                  <Input
                    data-testid="care-form-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(555) 123-4567"
                    className="mt-2 bg-white border-[#D5DACF] rounded-xl h-12"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#4A5D54]">
                    Service of interest
                  </Label>
                  <Select
                    value={form.service_interest}
                    onValueChange={(v) => update("service_interest")(v)}
                  >
                    <SelectTrigger
                      data-testid="care-form-service"
                      className="mt-2 bg-white border-[#D5DACF] rounded-xl h-12"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {site.services.map((s) => (
                        <SelectItem key={s.key} value={s.title}>
                          {s.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-[#4A5D54]">
                  Tell us about your loved one
                </Label>
                <Textarea
                  rows={5}
                  data-testid="care-form-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="A few lines about who you're caring for, their routine, and what they need help with..."
                  className="mt-2 bg-white border-[#D5DACF] rounded-xl"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="care-form-submit"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#C86A51] hover:bg-[#B35A42] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-full px-10 py-4 font-medium transition-colors shadow-sm"
                >
                  {submitting ? "Sending..." : "Request Care"}
                  {!submitting && <ArrowRight size={18} />}
                </button>

                <div className="inline-flex items-center gap-2 rounded-full bg-[#E6EAE3] border border-[#D5DACF] px-4 py-2 text-sm text-[#284A3B]">
                  <span className="w-2 h-2 rounded-full bg-[#C86A51] animate-pulse" />
                  Reply within one business day
                </div>
              </div>

              <p className="text-xs text-[#4A5D54]">
                By submitting you consent to be contacted about your care
                needs. Your information is kept confidential.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
